// WHAT EACH GRAIN IS CALLED, in one place.
//
// ⚠️ WHY THIS FILE EXISTS. These four nouns were literals in ELEVEN maps across EIGHT files in this
// directory — Icicle, ScriptSnout, RollupPanel (x2), rollupData (x3), TableView, AxisPatterns,
// FieldStanding — and `script_type` appeared in NONE of them. Every one of those maps therefore
// asserted "this movie is a screenplay", which is the same defect as the 'movie_act' / 'hero goal'
// literals on the PHP side: not wrong for the format it was written for, silently wrong for every
// other one. A stage play's middle grain is stored in `sequences` only because there are exactly
// three levels under script and the printed scene is the middle one. That is a POSITION. The table
// name must not reach the screen.
//
// ⚠️ THE FALLBACK IS THE SAFETY PROPERTY, NOT A CONVENIENCE. FEATURE below is character-for-character
// what all eleven maps held before this file existed. A payload with no `grain_nouns` — an older
// cached response, a fixture, a snapshot, a controller path that was never updated — resolves to it.
// So a screenplay cannot move unless someone edits FEATURE itself.
//
// The server (der_grain_nouns(), utils.php) is the source of truth and ships this on
// ratings.movie.grain_nouns. This file must agree with it for 'feature'; grainNouns.selftest.mjs
// asserts the shape, and the strings are pinned there too.

export const FEATURE = {
  movie:    { title: 'Script',    one: 'Whole',    plural: 'script',    singular: 'script' },
  act:      { title: 'Acts',      one: 'Act',      plural: 'acts',      singular: 'act' },
  sequence: { title: 'Sequences', one: 'Sequence', plural: 'sequences', singular: 'sequence' },
  scene:    { title: 'Scenes',    one: 'Scene',    plural: 'scenes',    singular: 'scene' },
};

// 'script' is how RollupPanel/FieldStanding key the top grain; 'movie' is how Icicle/prepareChartData
// do. Same grain, two spellings already in the tree, so both resolve rather than one of them
// silently returning undefined.
const ALIAS = { script: 'movie', overview: null };

/**
 * @param {object} script  ratings.movie — may be null/undefined/stale, which is the point.
 * @returns {{movie,act,sequence,scene}} always complete; missing keys fall back to FEATURE.
 */
export function grainNouns(script) {
  const supplied = script && script.grain_nouns;
  if (!supplied || typeof supplied !== 'object') return FEATURE;
  const out = {};
  for (const k of Object.keys(FEATURE)) {
    const s = supplied[k];
    out[k] = (s && typeof s === 'object') ? { ...FEATURE[k], ...s } : FEATURE[k];
  }
  return out;
}

function resolveKey(grain) {
  if (grain in FEATURE) return grain;
  return Object.prototype.hasOwnProperty.call(ALIAS, grain) ? ALIAS[grain] : null;
}

/** Plural, capitalised — the left rail and the tab headings. */
export function grainTitle(script, grain, fallback = '') {
  const k = resolveKey(grain);
  return k ? grainNouns(script)[k].title : fallback;
}

/** Singular, capitalised — column headers. */
export function grainOne(script, grain, fallback = '') {
  const k = resolveKey(grain);
  return k ? grainNouns(script)[k].one : fallback;
}

/** Lowercase, for prose: "across 12 units". */
export function grainPlural(script, grain, fallback = '') {
  const k = resolveKey(grain);
  return k ? grainNouns(script)[k].plural : fallback;
}

export function grainSingular(script, grain, fallback = '') {
  const k = resolveKey(grain);
  return k ? grainNouns(script)[k].singular : fallback;
}
